'use client';

/**
 * "Let your browser's assistant work in your apps?" (ADR-0049 "Open before
 * production"; owner decision 2026-10-01: build it).
 *
 * The one host dialog of the WebMCP rail, in the shape of ADR-0048's
 * WidgetOperateConsent: portal chrome drawn OVER the apps, never inside a
 * frame, every word the platform's. Mounted once in workspace-shell.tsx
 * right after `WebMcpToolsBridge`, which is the only thing that opens it.
 * Opened by a window CustomEvent (WEBMCP_CONSENT_EVENT) whose detail carries
 * a `resolve`; `askWebMcpConsent()` is the helper that dispatches one and
 * turns the person's answer into a promise.
 *
 * WHAT THE ANSWER MEANS. `allowed` and `declined` are the two buttons;
 * Escape and the backdrop are Not now. `unanswered` is a dialog that gave up
 * (WEBMCP_CONSENT_DIALOG_TTL_MS with nobody answering, the page unmounting
 * it) or no dialog listening at all. The registrar waits far less than the
 * dialog lives (CONSENT_WAIT_MS, inside the 30 s Chrome gives a tool), and
 * that is deliberate: when the caller's wait runs out the dialog STAYS OPEN,
 * so the person can still answer, and the answer is recorded for the next
 * call rather than lost with the one that stopped waiting.
 *
 * ONE DIALOG AT A TIME. A second ask while one is open joins it: both
 * resolvers hear the one answer. The registrar already shares one ask among
 * concurrent calls; this is the dialog's own half of the same rule, so no
 * sequence of asks can stack two questions on the person.
 *
 * NOBODY ANSWERS IT BY ACCIDENT (S-383, 2026-10-01). The dialog opens when
 * the browser's assistant calls a tool, a moment the person does not choose,
 * and Allow is a standing permission for every app they open. The first
 * build put the keyboard on Allow, the way its siblings focus their confirm
 * button, so a person who was typing when it opened granted it with their
 * next Enter or Space. Now:
 *   - the keyboard goes to the DIALOG, on neither button. Enter and Space do
 *     nothing there; Tab reaches Not now first and Allow second, and stays
 *     inside the dialog; Escape is Not now.
 *   - an Allow that lands within WEBMCP_CONSENT_ARM_MS of the dialog opening
 *     is ignored: it was aimed at whatever was on screen before. Declining
 *     is never held back, because declining is the safe answer.
 *   - `role="dialog"` and `aria-modal` sit on the panel itself, so assistive
 *     technology treats the page behind as out of reach while it is open.
 *   - Escape answers this dialog and does nothing else. The keys are heard
 *     on `window` in the CAPTURE phase and Escape is consumed there
 *     (`preventDefault` and `stopPropagation`), because whatever is open
 *     underneath listens on `document`: with Settings open beneath it, one
 *     Escape used to decline the question AND close Settings (found by the
 *     browser re-check, 2026-10-01). The topmost thing owns the key.
 * Taking the keyboard from the app is deliberate for the same reason: while
 * the question is open, what the person types must not land in the app the
 * assistant is about to work in.
 *
 * Focus goes back to the app frame that held it when the ask arrived
 * (`useAppFrameFocusReturn`, round 7), because a tool call is usually the
 * person's own assistant acting on the app they are looking at.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { useAppFrameFocusReturn } from '@/components/workspace/app-frame-focus-return';
import { cn } from '@/lib/utils';
import type { WebMcpConsentAnswer } from '@/lib/workspaces/webmcp-consent';

export const WEBMCP_CONSENT_EVENT = 'webmcp-consent';

/** A dialog nobody answers closes itself after this long, as `unanswered`. */
export const WEBMCP_CONSENT_DIALOG_TTL_MS = 2 * 60_000;

/** An Allow sooner than this after the dialog opened was not an answer to it. */
export const WEBMCP_CONSENT_ARM_MS = 500;

const BUTTON_FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

interface ConsentRequestDetail {
  resolve: (answer: WebMcpConsentAnswer) => void;
  /** Set by the dialog, synchronously, so the asker knows one is listening. */
  handled?: boolean;
}

/**
 * Ask the person through the dialog. Resolves when they answer, `unanswered`
 * when the dialog gives up, and `unanswered` at once when no dialog is
 * mounted on this page (nothing would ever answer). Never rejects.
 */
export function askWebMcpConsent(): Promise<WebMcpConsentAnswer> {
  if (typeof window === 'undefined') return Promise.resolve('unanswered');
  return new Promise<WebMcpConsentAnswer>((resolve) => {
    let settled = false;
    const settle = (answer: WebMcpConsentAnswer) => {
      if (settled) return;
      settled = true;
      resolve(answer);
    };
    const detail: ConsentRequestDetail = { resolve: settle };
    window.dispatchEvent(new CustomEvent(WEBMCP_CONSENT_EVENT, { detail }));
    if (!detail.handled) settle('unanswered');
  });
}

export function WebMcpConsentDialog() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const waiters = useRef<Array<(answer: WebMcpConsentAnswer) => void>>([]);
  const openedAt = useRef(0);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const captureAppFocus = useAppFrameFocusReturn(open);

  // The panel takes the keyboard when it is drawn. A callback ref, because
  // the portal draws it one render after `open` turns true: an effect on
  // `open` would find nothing to focus.
  const takeFocus = useCallback((el: HTMLDivElement | null) => {
    panelRef.current = el;
    el?.focus({ preventScroll: true });
  }, []);

  const finish = useCallback((answer: WebMcpConsentAnswer) => {
    const list = waiters.current;
    waiters.current = [];
    setOpen(false);
    for (const resolve of list) {
      try {
        resolve(answer);
      } catch {
        // one asker's failure never loses another's answer
      }
    }
  }, []);

  const allow = useCallback(() => {
    if (Date.now() - openedAt.current < WEBMCP_CONSENT_ARM_MS) return;
    finish('allowed');
  }, [finish]);

  useEffect(() => {
    const onAsk = (e: Event) => {
      const detail = (e as CustomEvent<ConsentRequestDetail>).detail;
      if (!detail || typeof detail.resolve !== 'function') return;
      detail.handled = true;
      captureAppFocus();
      // A second ask while the dialog is open joins it: one question, one
      // answer, and the clock on Allow keeps running from the first.
      if (waiters.current.length === 0) openedAt.current = Date.now();
      waiters.current.push(detail.resolve);
      setOpen(true);
    };
    window.addEventListener(WEBMCP_CONSENT_EVENT, onAsk);
    return () => window.removeEventListener(WEBMCP_CONSENT_EVENT, onAsk);
  }, [captureAppFocus]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Consumed here: nothing underneath may also act on it.
        e.preventDefault();
        e.stopPropagation();
        finish('declined');
        return;
      }
      if (e.key !== 'Tab') return;
      // Tab stays inside the dialog: Not now, Allow, and round again.
      const panel = panelRef.current;
      if (!panel) return;
      const stops = Array.from(panel.querySelectorAll<HTMLElement>('button:not([disabled])'));
      if (stops.length === 0) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const onStop = !!active && stops.includes(active);
      const leaving = e.shiftKey ? !onStop || active === first : !onStop || active === last;
      if (!leaving) return;
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    };
    // Capture phase on `window`: first to hear the key, before any listener
    // on `document` (the panels, Settings, the canvas).
    window.addEventListener('keydown', onKey, true);
    // Counted from the first ask: a later ask that joined does not extend it.
    const ttl = setTimeout(() => finish('unanswered'), WEBMCP_CONSENT_DIALOG_TTL_MS);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      clearTimeout(ttl);
    };
  }, [open, finish]);

  // The page is leaving: nobody will answer the askers still waiting.
  useEffect(
    () => () => {
      const list = waiters.current;
      waiters.current = [];
      for (const resolve of list) resolve('unanswered');
    },
    [],
  );

  if (!open) return null;

  return (
    <PopoverPortal
      role="presentation"
      style={{ position: 'fixed', inset: 0, zIndex: 240, display: 'flex', justifyContent: 'center' }}
      className="items-end bg-black/40 backdrop-blur-sm md:items-start md:py-[14vh]"
    >
      <div aria-hidden className="absolute inset-0" onClick={() => finish('declined')} />
      <div
        ref={takeFocus}
        role="dialog"
        aria-modal="true"
        aria-label={t('workspace.webmcp.consent.ariaLabel')}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'glass-panel relative flex w-full flex-col overflow-hidden shadow-2xl outline-none',
          'max-h-[90vh] rounded-t-2xl',
          'md:w-[min(460px,96vw)] md:rounded-2xl',
        )}
      >
        <div className="min-h-0 overflow-y-auto px-4 py-3">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Sparkles className="h-3 w-3" /> {t('workspace.webmcp.consent.eyebrow')}
          </div>
          <div className="mt-1 break-words text-sm font-semibold tracking-tight">{t('workspace.webmcp.consent.title')}</div>
          <p className="mt-1.5 text-pretty text-xs text-muted-foreground">{t('workspace.webmcp.consent.body')}</p>
          <p className="mt-1.5 text-pretty text-xs text-muted-foreground">{t('workspace.webmcp.consent.keeps')}</p>
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2 px-4 py-3 shadow-[inset_0_1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_1px_0_rgba(0,0,0,0.35)]">
          <button
            type="button"
            onClick={() => finish('declined')}
            className={cn('rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/50', BUTTON_FOCUS)}
          >
            {t('workspace.webmcp.consent.cancel')}
          </button>
          <button
            type="button"
            onClick={allow}
            className={cn('rounded-lg bg-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground', BUTTON_FOCUS)}
          >
            {t('workspace.webmcp.consent.confirm')}
          </button>
        </div>
      </div>
    </PopoverPortal>
  );
}
